import jwt from "jwt-decode";
import Auth from "@aws-amplify/auth";
import {DateTime} from "luxon";

/**
 * authUtil.js - Secure Authentication Utilities
 * 
 * SECURITY UPDATES (HIPAA Compliance):
 * - REMOVED: All localStorage usage for token storage
 * - REMOVED: All sessionStorage usage by default (memory-first cache)
 * - CHANGED: Tokens are fetched from Cognito session and cached in-memory with expiry
 * - ADDED: Tokens are never persisted to any client-side storage
 * 
 * How it works:
 * - All token access goes through Auth.currentSession()
 * - Cognito SDK manages token refresh internally
 * - Tokens exist only in memory during the session
 * - Page refresh requires re-authentication (security feature)
 */

const TOKEN_EXPIRY_BUFFER_SECONDS = 120;
const COGNITO_GROUPS = 'cognito:groups';

let cachedAccessToken = null;
let cachedAccessTokenExp = null;
let cachedIdToken = null;
let cachedIdTokenExp = null;

const isTokenFresh = (exp) => {
    if (!exp) return false;
    return (exp - DateTime.now().toSeconds()) > TOKEN_EXPIRY_BUFFER_SECONDS;
};

export const clearAuthTokenCache = () => {
    cachedAccessToken = null;
    cachedAccessTokenExp = null;
    cachedIdToken = null;
    cachedIdTokenExp = null;
};

// Role constants - exported for use throughout the app
export const ROLE_NAME = {
    CLINICIAN: 'clinicians',
    SUPER_ADMIN: 'super_admin',
    AUDITOR_READ_ONLY: 'auditor_read_only',
    STAFF: 'staff',
    OPERATION_SUPPORT: 'operation_support',
};

export const ROLES = [
    ROLE_NAME.AUDITOR_READ_ONLY, 
    ROLE_NAME.CLINICIAN, 
    ROLE_NAME.SUPER_ADMIN, 
    ROLE_NAME.STAFF, 
    ROLE_NAME.OPERATION_SUPPORT
];

export const CLINICIAN_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};

/**
 * Get the logged-in person's full name from the ID token
 * @returns {Promise<string>} Full name or 'UNKNOWN UNKNOWN' if not available
 */
export const getLoggedInPersonName = async () => {
    const token = await decodeIdToken();
    const firstName = token?.['given_name']?.length > 0 ? token['given_name'] : 'UNKNOWN';
    const lastName = token?.['family_name']?.length > 0 ? token['family_name'] : 'UNKNOWN';
    return firstName + ' ' + lastName;
};

/**
 * Get user's Cognito groups/roles
 * @returns {Promise<string[]>} Array of role names
 */
export const getUserRoles = async () => {
    const token = await decodeIdToken();
    return token?.[COGNITO_GROUPS] || [];
};

/**
 * Check if user has CLINICIAN role
 */
export const isClinicianRole = async () => {
    const token = await decodeIdToken();
    return token?.[COGNITO_GROUPS]?.includes(ROLE_NAME.CLINICIAN) || false;
};

/**
 * Check if user has SUPER_ADMIN role
 */
export const isSuperAdminRole = async () => {
    const token = await decodeIdToken();
    return token?.[COGNITO_GROUPS]?.includes(ROLE_NAME.SUPER_ADMIN) || false;
};

/**
 * Check if user has AUDITOR_READ_ONLY role
 */
export const isAuditorReadOnlyRole = async () => {
    const token = await decodeIdToken();
    return token?.[COGNITO_GROUPS]?.includes(ROLE_NAME.AUDITOR_READ_ONLY) || false;
};

/**
 * Check if user has STAFF role
 */
export const hasStaffRole = async () => {
    const token = await decodeIdToken();
    return token?.[COGNITO_GROUPS]?.includes(ROLE_NAME.STAFF) || false;
};

/**
 * Check if user has OPERATION_SUPPORT role
 */
export const isOperationSupportRole = async () => {
    const token = await decodeIdToken();
    return token?.[COGNITO_GROUPS]?.includes(ROLE_NAME.OPERATION_SUPPORT) || false;
};

/**
 * Check if user has access to Product and Order data
 */
export const hasAccessToProductAndOrderData = async () => {
    const [staff, clinician, superAdmin] = await Promise.all([
        hasStaffRole(), 
        isClinicianRole(), 
        isSuperAdminRole()
    ]);
    return staff || clinician || superAdmin;
};

/**
 * Check if user has access to Clinical data
 */
export const hasAccessToClinicalData = async () => {
    const [isSuperAdmin, isClinician] = await Promise.all([
        isSuperAdminRole(), 
        isClinicianRole()
    ]);
    return isSuperAdmin || isClinician;
};

/**
 * Get the clinician ID (Cognito sub) from the current session
 * @returns {Promise<string|null>} Clinician ID or null
 */
export const clinicianIdFromRefreshAuth = async () => {
    const decoded = await decodeIdToken();
    return decoded?.sub || null;
};

/**
 * Decode a JWT and return exp when present
 * @param {string} token
 * @returns {number|null} Expiration timestamp or null
 */
export const getTokenExp = (token) => {
    if (!token) return null;
    try {
        const decoded = jwt(token);
        return decoded?.exp || null;
    } catch (err) {
        console.error('Error decoding token exp:', err);
        return null;
    }
};

/**
 * Get raw access token JWT string
 * SECURITY: Fetches directly from Cognito session, no localStorage
 * @returns {Promise<string|null>} JWT string or null
 */
export const getAccessToken = async () => {
    if (cachedAccessToken && isTokenFresh(cachedAccessTokenExp)) {
        return cachedAccessToken;
    }

    try {
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken();
        const jwtToken = accessToken.getJwtToken();
        const exp = accessToken.getExpiration();

        cachedAccessToken = jwtToken;
        cachedAccessTokenExp = exp;
        return jwtToken;
    } catch (err) {
        console.error('Error getting access token:', err);
        return null;
    }
};

/**
 * Get raw ID token JWT string
 * SECURITY: Fetches directly from Cognito session, no localStorage
 * @returns {Promise<string|null>} JWT string or null
 */
export const getIdToken = async () => {
    if (cachedIdToken && isTokenFresh(cachedIdTokenExp)) {
        return cachedIdToken;
    }

    try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken();
        const jwtToken = idToken.getJwtToken();
        const exp = idToken.getExpiration();

        cachedIdToken = jwtToken;
        cachedIdTokenExp = exp;
        return jwtToken;
    } catch (err) {
        // Session expired or user not authenticated
        console.error('Error getting ID token:', err);
        return null;
    }
};

/**
 * Get decoded ID token payload
 * SECURITY: ID token is UI-only; API auth uses access token.
 * @returns {Promise<object|null>} Decoded token payload or null
 */
export const decodeIdToken = async () => {
    const rawJwt = await getIdToken();
    if (!rawJwt) return null;
    
    try {
        return jwt(rawJwt);
    } catch (err) {
        console.error('Error decoding ID token:', err);
        return null;
    }
};

export const getDecodedIdToken = async () => {
    return decodeIdToken();
};

/**
 * Get raw access token JWT string
 * SECURITY: Fetches directly from Cognito session, no localStorage
 * @returns {Promise<string|null>} JWT string or null
 */
export const getAccessTokenJwt = async () => {
    return getAccessToken();
};

/**
 * Alias for getAccessTokenJwt (backward compatibility)
 */
export const getRawJwt = async () => {
    return getAccessToken();
};

/**
 * Get raw ID token JWT string
 * SECURITY: Fetches directly from Cognito session, no localStorage
 * @returns {Promise<string|null>} JWT string or null
 */
export const getIdTokenJwt = async () => {
    return getIdToken();
};

/**
 * Get the full logged-in token object
 * SECURITY: Fetches directly from Cognito session, no localStorage
 * @returns {Promise<object|null>} Token object with jwt, exp, issuedAt or null
 */
export const getLoggedInToken = async () => {
    try {
        const session = await Auth.currentSession();
        const idToken = session.getIdToken();
        return {
            jwt: idToken.getJwtToken(),
            exp: idToken.getExpiration(),
            issuedAt: idToken.getIssuedAt(),
        };
    } catch (err) {
        console.error('Error getting logged in token:', err);
        return null;
    }
};

/**
 * Get the full access token object
 * SECURITY: Fetches directly from Cognito session, no localStorage
 * @returns {Promise<object|null>} Token object with jwt, exp, issuedAt or null
 */
export const getLoggedInAccessToken = async () => {
    try {
        const session = await Auth.currentSession();
        const accessToken = session.getAccessToken();
        return {
            jwt: accessToken.getJwtToken(),
            exp: accessToken.getExpiration(),
            issuedAt: accessToken.getIssuedAt(),
        };
    } catch (err) {
        console.error('Error getting access token:', err);
        return null;
    }
};

/**
 * Clear authentication state
 * SECURITY: No localStorage to clear - this is now a no-op
 * Kept for backward compatibility with existing code
 * 
 * @deprecated Use Auth.signOut() directly instead
 */
export const clearIdToken = () => {
    // No-op: We no longer store tokens in localStorage
    // Auth.signOut() should be used to clear the Cognito session
    clearAuthTokenCache();
    console.log('clearIdToken called - no localStorage to clear (security feature)');
};

/**
 * Check if a JWT is valid (not expired)
 * @param {string} jwtToken - The JWT string
 * @param {number} exp - Expiration timestamp
 * @returns {boolean} True if valid
 */
export const isValidJwt = (jwtToken, exp) => {
    if (!jwtToken || !exp) return false;
    // Make sure token has at least 2 mins before expiry
    return isTokenFresh(exp);
};

/**
 * Check if user is currently authenticated
 * @returns {Promise<boolean>} True if authenticated
 */
export const isAuthenticated = async () => {
    try {
        await Auth.currentSession();
        return true;
    } catch (err) {
        return false;
    }
};
